import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/layout/PageShell';
import {
  CreditCard, Building2, Zap,
  Lock, Shield, Bot,
  Target, Plane, Shuffle,
  Users, Package, Coffee,
  X, Handshake, ClipboardList,
  KeyRound, ArrowRight,
} from 'lucide-react';

const capabilities = [
  {
    category: 'Customer Identity (CIAM)',
    Icon: CreditCard,
    items: [
      {
        title: 'Auth0 B2C / B2B / B2B2C',
        desc: 'Customer-facing identity for consumer and business apps.',
        Icon: CreditCard,
        detail: 'Auth0 provides a flexible, developer-friendly identity platform for building secure login experiences across consumer (B2C), business (B2B), and hybrid (B2B2C) models. RAAH helps partners architect, implement, and optimize Auth0 deployments — from universal login to custom flows, MFA, and social connections.',
        highlights: ['Universal Login & Branding', 'Social & Enterprise Connections', 'Custom Actions & Flows', 'Multi-tenant Architecture', 'MFA & Adaptive Authentication'],
        useCases: 'Retail customer portals, SaaS multi-tenancy, partner ecosystem access, consumer loyalty apps.',
        productMatch: 'Auth0 (B2C/B2B/B2B2C)', useCaseMatch: 'CIAM',
      },
      {
        title: 'Okta CIS',
        desc: 'Enterprise CIAM for regulated industries and legacy environments.',
        Icon: Building2,
        detail: 'Okta Customer Identity Solution (CIS) delivers enterprise-grade CIAM for organizations operating in regulated industries such as financial services, healthcare, and government. RAAH brings deep expertise in deploying Okta CIS alongside legacy identity systems, ensuring compliance and continuity.',
        highlights: ['Regulatory Compliance (HIPAA, PCI, FedRAMP)', 'Legacy System Integration', 'Progressive Profiling', 'Consent & Privacy Management', 'High-Availability Architecture'],
        useCases: 'Healthcare patient portals, financial services digital banking, government citizen services.',
        productMatch: 'Okta CIS', useCaseMatch: 'CIAM',
      },
      {
        title: 'CIAM Migration (BOLT)',
        desc: 'Accelerated migration tooling for moving identity to Auth0 or Okta.',
        Icon: Zap,
        detail: 'BOLT is RAAH\'s proprietary migration accelerator that dramatically reduces the time and risk of moving from legacy identity providers to Auth0 or Okta. It handles bulk user migration, password hash portability, attribute mapping, and cutover orchestration.',
        highlights: ['Bulk User Migration', 'Password Hash Portability', 'Zero-Downtime Cutover', 'Attribute & Claim Mapping', 'Rollback & Safety Net'],
        useCases: 'Legacy IdP replacements, platform consolidations, acquisitions and divestitures.',
        useCaseMatch: 'Migration',
      },
    ],
  },
  {
    category: 'Workforce Identity',
    Icon: Users,
    items: [
      {
        title: 'Okta WIC',
        desc: 'Workforce identity, SSO, MFA, and lifecycle management.',
        Icon: Lock,
        detail: 'Okta Workforce Identity Cloud (WIC) is the gold standard for enterprise workforce identity. RAAH partners with organizations to deploy Okta WIC for SSO, adaptive MFA, automated provisioning/deprovisioning, and deep integrations with HR systems and cloud applications.',
        highlights: ['Single Sign-On (SSO)', 'Adaptive MFA', 'Lifecycle Management', 'HR-Driven Provisioning', 'Zero Trust Integration'],
        useCases: 'Enterprise SSO rollouts, HR system integrations, M&A identity consolidations, Zero Trust modernization.',
        productMatch: 'Okta WIC', useCaseMatch: 'Workforce IAM',
      },
      {
        title: 'IGA (Governance)',
        desc: 'Identity governance and administration.',
        Icon: Shield,
        detail: 'RAAH delivers comprehensive Identity Governance & Administration (IGA) solutions. From access certifications and role management to entitlement discovery and audit reporting, we help organizations reduce risk and meet compliance requirements.',
        highlights: ['Access Certifications & Reviews', 'Role-Based Access Control (RBAC)', 'Entitlement Discovery', 'Access Recertification', 'Audit & Compliance Reporting'],
        useCases: 'SOX/SOC2 compliance, access recertification automation, entitlement cleanup.',
        useCaseMatch: 'IGA (Governance)',
      },
      {
        title: 'PAM',
        desc: 'Privileged access management.',
        Icon: KeyRound,
        detail: 'RAAH delivers Privileged Access Management (PAM) solutions covering just-in-time privileged access, session recording, and credential vaulting. We help organizations lock down their most sensitive accounts and meet audit requirements.',
        highlights: ['Just-in-Time Privileged Access', 'Session Recording & Monitoring', 'Credential Vaulting', 'Least-Privilege Enforcement', 'Audit & Compliance Reporting'],
        useCases: 'Privileged account hardening, break-glass access workflows, PAM tool consolidation.',
        useCaseMatch: 'PAM',
      },
      {
        title: 'AI Identity',
        desc: 'Emerging AI-first identity patterns and implementations.',
        Icon: Bot,
        detail: 'As AI agents, copilots, and autonomous systems become part of the enterprise, identity must evolve. RAAH is at the forefront of AI Identity — securing non-human identities, managing machine-to-machine trust, and enabling safe AI agent access to enterprise resources.',
        highlights: ['Non-Human Identity (NHI) Management', 'Machine-to-Machine (M2M) Auth', 'AI Agent Access Controls', 'Secrets & Credential Management', 'Identity for LLM Integrations'],
        useCases: 'Copilot deployments, autonomous agent pipelines, AI-driven API access, LLM-integrated enterprise apps.',
        useCaseMatch: 'AI Identity',
      },
    ],
  },
  {
    category: 'Delivery Models',
    Icon: Target,
    items: [
      {
        title: 'Pre-Sales Architecture',
        desc: 'Technical scoping, demos, and solution design before the close.',
        Icon: Target,
        detail: 'RAAH embeds with your sales team to provide technical credibility and accelerate deals. Our pre-sales architects lead discovery sessions, build custom demos, produce architecture diagrams, and write solution briefs — giving your team the edge needed to win complex identity opportunities.',
        highlights: ['Discovery & Technical Scoping', 'Custom Demo Environments', 'Solution Architecture Diagrams', 'RFP & Proposal Support', 'POC Delivery'],
        useCases: 'Enterprise sales cycles, competitive displacements, multi-product opportunities.',
      },
      {
        title: 'In-Flight Delivery',
        desc: 'Hands-on support during active implementation.',
        Icon: Plane,
        detail: 'Once a deal is closed, RAAH transitions seamlessly into delivery. Our in-flight support model provides hands-on engineers and architects who integrate with your customer\'s team to ensure successful implementation, knowledge transfer, and go-live readiness.',
        highlights: ['Staff Augmentation', 'Implementation QA & Review', 'Customer-Facing Workshops', 'Go-Live Readiness Assessments', 'Post-Launch Hypercare'],
        useCases: 'Complex multi-phase rollouts, at-risk implementations, accelerated timelines.',
      },
      {
        title: 'WATT',
        desc: 'Integration platform for complex identity use cases.',
        Icon: Shuffle,
        detail: 'WATT acts as an intelligent identity broker — sitting between your IdP and applications to handle complex transformation logic, legacy protocol bridging, and multi-source attribute aggregation that native platforms cannot support out of the box.',
        highlights: ['Protocol Translation (SAML, OIDC, WS-Fed)', 'Attribute Aggregation & Transformation', 'Legacy System Bridging', 'Custom Claims Engineering', 'Multi-IdP Orchestration'],
        useCases: 'Legacy app modernization, multi-IdP environments, complex claims requirements, regulated industries.',
      },
    ],
  },
];

const caseStudies = [
  {
    client: 'UPS',
    Icon: Package,
    desc: 'Large-scale workforce identity modernization.',
    detail: 'RAAH partnered with UPS to modernize their global workforce identity infrastructure, migrating tens of thousands of employees from a legacy on-premise IdP to Okta WIC. The engagement included SSO for 200+ applications, adaptive MFA rollout, and integration with SAP HR for automated lifecycle management.',
    highlights: ['200+ Application SSO Integrations', 'Global MFA Rollout', 'SAP HR Lifecycle Automation', 'Zero-Downtime Migration', 'Phased Rollout Across Regions'],
    outcome: 'Reduced identity-related helpdesk tickets by 60% and achieved full compliance with internal Zero Trust mandate within 9 months.',
  },
  {
    client: 'Wawa',
    Icon: Coffee,
    desc: 'Retail CIAM for customer loyalty and digital engagement.',
    detail: 'RAAH designed and delivered a customer identity solution for Wawa\'s digital loyalty platform, enabling millions of customers to register, authenticate, and manage their accounts across mobile and web. The solution used Auth0 with custom branding, social login, and progressive profiling.',
    highlights: ['Millions of Customer Identities', 'Mobile & Web Unified Login', 'Social Login Integration', 'Progressive Profiling', 'Loyalty Platform Integration'],
    outcome: 'Launched new loyalty app with seamless identity experience, achieving 40% faster registration completion and a significant reduction in abandoned signups.',
  },
];

const s = {
  section:      { marginBottom: '28px' },
  catHeader:    { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' },
  catIconWrap:  { width: '28px', height: '28px', borderRadius: '6px', background: '#f0f0ee', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', flexShrink: 0 },
  catTitle:     { fontSize: '14px', fontWeight: '700', color: '#333' },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' },
  item:         { border: '1px solid #e5e5e5', borderRadius: '8px', padding: '14px', cursor: 'pointer', transition: 'all 0.15s', background: '#fff' },
  itemIconWrap: { width: '36px', height: '36px', borderRadius: '8px', background: '#fef0e6', color: '#fd5f0d', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' },
  itemTitle:    { fontSize: '13px', fontWeight: '600', marginBottom: '4px' },
  itemDesc:     { fontSize: '12px', color: '#666', marginBottom: '8px' },
  learnMore:    { display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '11px', color: '#fd5f0d', fontWeight: '600' },
  divider:      { borderTop: '1px solid #e5e5e5', margin: '20px 0' },
  caseGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' },
  caseCard:     { border: '1px solid #e5e5e5', borderRadius: '8px', padding: '14px', cursor: 'pointer', background: '#fff', transition: 'all 0.15s' },
  caseIconWrap: { width: '40px', height: '40px', borderRadius: '10px', background: '#f0f0ee', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' },

  // Modal
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal:        { background: '#fff', borderRadius: '12px', padding: '32px', maxWidth: '600px', width: '100%', maxHeight: '85vh', overflowY: 'auto', position: 'relative' },
  modalClose:   { position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: '#999', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalIconWrap:{ width: '48px', height: '48px', borderRadius: '12px', background: '#fef0e6', color: '#fd5f0d', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' },
  modalTitle:   { fontSize: '18px', fontWeight: '700', color: '#111', marginBottom: '4px' },
  modalDesc:    { fontSize: '13px', color: '#666', marginBottom: '16px' },
  modalDetail:  { fontSize: '13px', color: '#333', lineHeight: '1.7', marginBottom: '20px' },
  highlightList:{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' },
  highlight:    { padding: '4px 12px', background: '#f0f5ff', border: '1px solid #bac8ff', borderRadius: '20px', fontSize: '12px', color: '#1971c2', fontWeight: '500' },
  sectionLabel: { fontSize: '11px', fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' },
  outcomeBox:   { background: '#f8f9fa', borderRadius: '8px', padding: '14px', fontSize: '13px', color: '#333', lineHeight: '1.6', marginBottom: '20px' },
  engageBtn:    { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 24px', background: '#fd5f0d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', textDecoration: 'none' },
};

function CapabilityModal({ item, onClose, onEngage }) {
  const { Icon } = item;
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={20} strokeWidth={2} /></button>
        <div style={s.modalIconWrap}><Icon size={24} strokeWidth={1.75} /></div>
        <div style={s.modalTitle}>{item.title}</div>
        <div style={s.modalDesc}>{item.desc}</div>
        <div style={s.modalDetail}>{item.detail}</div>
        <div style={s.sectionLabel}>Key Capabilities</div>
        <div style={s.highlightList}>
          {item.highlights.map(h => <span key={h} style={s.highlight}>{h}</span>)}
        </div>
        <div style={s.sectionLabel}>Common Use Cases</div>
        <div style={{ ...s.outcomeBox, marginBottom: '24px' }}>{item.useCases}</div>
        <button style={s.engageBtn} onClick={onEngage}>
          <Handshake size={16} strokeWidth={1.75} /> Engage RAAH on This
        </button>
      </div>
    </div>
  );
}

function CaseStudyModal({ item, onClose, onEngage }) {
  const { Icon } = item;
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.modalClose} onClick={onClose}><X size={20} strokeWidth={2} /></button>
        <div style={s.modalIconWrap}><Icon size={24} strokeWidth={1.75} /></div>
        <div style={s.modalTitle}>{item.client}</div>
        <div style={s.modalDesc}>{item.desc}</div>
        <div style={s.modalDetail}>{item.detail}</div>
        <div style={s.sectionLabel}>Highlights</div>
        <div style={s.highlightList}>
          {item.highlights.map(h => <span key={h} style={s.highlight}>{h}</span>)}
        </div>
        <div style={s.sectionLabel}>Outcome</div>
        <div style={s.outcomeBox}>{item.outcome}</div>
        <button style={s.engageBtn} onClick={onEngage}>
          <Handshake size={16} strokeWidth={1.75} /> Engage RAAH on This
        </button>
      </div>
    </div>
  );
}

export default function Capabilities() {
  const navigate = useNavigate();
  const [selectedCap,  setSelectedCap]  = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);

  const handleEngage = () => {
    const item = selectedCap || selectedCase;
    setSelectedCap(null);
    setSelectedCase(null);
    navigate('/engage', {
      state: {
        interestLabel: item?.title || item?.client,
        product: item?.productMatch,
        useCase: item?.useCaseMatch,
      },
    });
  };

  return (
    <PageShell title="Explore Capabilities" desc="Learn what RAAH delivers and how we work with partners.">

      {capabilities.map(cap => (
        <div key={cap.category} style={s.section}>
          <div style={s.catHeader}>
            <div style={s.catIconWrap}><cap.Icon size={15} strokeWidth={1.75} /></div>
            <span style={s.catTitle}>{cap.category}</span>
          </div>
          <div style={s.grid}>
            {cap.items.map(item => (
              <div
                key={item.title}
                style={s.item}
                onClick={() => setSelectedCap(item)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#fd5f0d'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(253,95,13,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e5e5'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={s.itemIconWrap}><item.Icon size={18} strokeWidth={1.75} /></div>
                <div style={s.itemTitle}>{item.title}</div>
                <div style={s.itemDesc}>{item.desc}</div>
                <div style={s.learnMore}>Learn more <ArrowRight size={12} /></div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={s.divider} />
      <div style={s.catHeader}>
        <div style={s.catIconWrap}><ClipboardList size={15} strokeWidth={1.75} /></div>
        <span style={s.catTitle}>Case Studies</span>
      </div>
      <div style={s.caseGrid}>
        {caseStudies.map(cs => (
          <div
            key={cs.client}
            style={s.caseCard}
            onClick={() => setSelectedCase(cs)}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#fd5f0d'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(253,95,13,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e5e5'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={s.caseIconWrap}><cs.Icon size={20} strokeWidth={1.75} /></div>
            <div style={s.itemTitle}>{cs.client}</div>
            <div style={s.itemDesc}>{cs.desc}</div>
            <div style={s.learnMore}>Read more <ArrowRight size={12} /></div>
          </div>
        ))}
      </div>

      {selectedCap && (
        <CapabilityModal item={selectedCap} onClose={() => setSelectedCap(null)} onEngage={handleEngage} />
      )}
      {selectedCase && (
        <CaseStudyModal item={selectedCase} onClose={() => setSelectedCase(null)} onEngage={handleEngage} />
      )}
    </PageShell>
  );
}